/*
 * MADDOG Ritual builder — Phase IV.2B-R controller.
 *
 * Real Step 2 interaction redesigned: no more three independent
 * sliders. All interactions are ADJACENT-PAIR only — every action
 * changes at most two scents, keeping the untouched scent fixed.
 *
 * Ratio state model unchanged from IV.2B (for preview/Stage 3
 * compatibility):
 *   state.ratios = { productId: integerPercent }
 *   state.ratiosSelectionKey = 'id1,id2,id3'
 *
 * Defaults unchanged:
 *   2 scents  → 50 / 50
 *   3 scents  → 34 / 33 / 33
 *
 * Interaction primitives (`setRatioPair(action, value)`):
 *
 *   2 scents:
 *     - scent1 / handle1 : sets r1 = clamp(1..99, v);  r2 = 100 - r1
 *     - scent2           : sets r2 = clamp(1..99, v);  r1 = 100 - r2
 *
 *   3 scents:
 *     - handle1 : b1 clamp(1..99-r3, v);  r1 = b1;   r2 = 100-b1-r3;  r3 UNCHANGED
 *     - handle2 : b2 clamp(r1+1..99, v);  r2 = b2-r1; r3 = 100-b2;    r1 UNCHANGED
 *     - scent1  : r1 clamp(1..99-r3, v);  r2 = 100-r1-r3;             r3 UNCHANGED
 *     - scent3  : r3 clamp(1..99-r1, v);  r2 = 100-r1-r3;             r1 UNCHANGED
 *
 * The 3-scent middle scent (r2) is never independently editable —
 * it's an AUTO BALANCE derived value.
 *
 * All values remain integers; total remains exactly 100 by
 * construction; every selected scent stays ≥ 1%.
 */
(function () {
  'use strict';

  var MAX_SELECTIONS = 3;
  var MIN_TO_CONTINUE = 2;

  // Stage 3 constants (Phase IV.2C).
  var NAME_MAX_LEN = 24;
  var PRICE_PERFUME = 75;
  var PRICE_OIL = 45;
  var PRICE_FEE = 10;
  // Allowed characters for blend name: Latin letters (basic +
  // Latin-1 supplement + Latin extended), digits, spaces,
  // apostrophe, hyphen, period, ampersand. Emoji + line breaks
  // + control chars rejected.
  var NAME_ALLOWED_PATTERN = /^[A-Za-z0-9 '\.\-&À-ſ]+$/;

  // Small display helper used across Stage 3 pricing surfaces.
  function formatMoneyAUD(dollars) {
    var n = Number(dollars) || 0;
    return '$' + n.toFixed(2) + ' AUD';
  }

  // Recipe ID: RITUAL-{8 chars} using cryptographically-random bytes.
  // Alphabet excludes 0/O/I/1 to avoid ambiguous glyphs on the eventual
  // handwritten receipt / staff order sheet.
  var RECIPE_ID_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  function generateRecipeId() {
    var bytes = new Uint8Array(8);
    (window.crypto || window.msCrypto).getRandomValues(bytes);
    var out = '';
    for (var i = 0; i < 8; i++) {
      out += RECIPE_ID_ALPHABET[bytes[i] % RECIPE_ID_ALPHABET.length];
    }
    return 'RITUAL-' + out;
  }

  function init() {
    var dataEl = document.getElementById('maddog-ritual-fragrance-data');
    var cardList = document.querySelector('[data-ritual-card-list]');
    var workspace = document.querySelector('[data-ritual-workspace]');
    if (!dataEl || !cardList || !workspace) return;

    var data;
    try { data = JSON.parse(dataEl.textContent || dataEl.innerText || '{}'); }
    catch (e) { return; }
    if (!data || typeof data !== 'object') return;

    var els = collectElements();
    if (els.cardItems.length === 0) return;

    var state = {
      selection: [],           // ordered array of product-ID strings
      letter: '',              // '' | 'M' | 'F' | 'U'
      query: '',
      stage: 1,
      ratios: {},              // productId → integer 1..99
      ratiosSelectionKey: '',  // fingerprint of the selection ratios were computed for
      // Stage 3 state
      blendName: '',           // string, trimmed on validation
      primaryFormat: null,     // null | 'perfume' | 'oil'
      addMatchingFormat: false,
      reviewMode: false,       // false = editable Stage 3, true = final review inside Stage 3
      // Cart integration state
      cartPending: false,      // true while /cart/add.js request in flight
      commerce: parseCommerceData()   // { perfumeVariantId, oilVariantId, setVariantId, prices, ok }
    };

    // Per-row balance element references (id → { row, slider, numInput })
    var balanceRowRefs = {};

    updateMfuCounts();

    // ---- Wire Stage 1 (unchanged from IV.2A-R) ---------------

    els.cardItems.forEach(function (item) {
      var btn = item.querySelector('[data-ritual-select]');
      if (!btn) return;
      btn.addEventListener('click', function () {
        onCardToggle(item.getAttribute('data-ritual-card-id'));
      });
    });

    if (els.finder) {
      els.finder.addEventListener('input', function () {
        state.query = String(this.value || '').trim().toLowerCase();
        toggleSearchClear();
        applyView();
      });
    }

    // Delegated Clear Search — attached to any [data-ritual-search-clear]
    // (the field × button and the empty-state Clear button both use it).
    // Search only affects visibility; selected scents and formula
    // state are preserved (they live in state.selection, not in the DOM).
    document.addEventListener('click', function (e) {
      var t = e.target;
      if (!t || !t.closest) return;
      if (!t.closest('[data-ritual-search-clear]')) return;
      e.preventDefault();
      if (els.finder) {
        els.finder.value = '';
        state.query = '';
        toggleSearchClear();
        applyView();
        els.finder.focus();
      }
    });

    function toggleSearchClear() {
      var clear = document.querySelector('.__search-clear[data-ritual-search-clear]');
      if (!clear) return;
      clear.hidden = !(els.finder && els.finder.value);
    }

    els.mfuButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        state.letter = btn.getAttribute('data-ritual-mfu-value') || '';
        els.mfuButtons.forEach(function (b) {
          var pressed = (b === btn);
          b.setAttribute('aria-pressed', pressed ? 'true' : 'false');
          b.classList.toggle('is-active', pressed);
        });
        applyView();
      });
    });

    // Delegated slot-remove (Stage 1 sidebar) + stage-3 summary rows
    document.addEventListener('click', function (e) {
      var target = e.target;
      if (!target || !target.closest) return;
      var removeBtn = target.closest('[data-ritual-blend-remove]');
      if (!removeBtn) return;
      e.preventDefault();
      var id = removeBtn.getAttribute('data-ritual-blend-remove');
      var idx = state.selection.indexOf(id);
      if (idx >= 0) {
        state.selection.splice(idx, 1);
        applyView();
      }
    });

    // Continue button — stage-aware dispatch
    if (els.continueBtn) {
      els.continueBtn.addEventListener('click', function () {
        if (state.stage === 1) {
          var n = state.selection.length;
          if (n < MIN_TO_CONTINUE || n > MAX_SELECTIONS) return;
          goToStage(2);
        } else if (state.stage === 2) {
          goToStage(3);
        } else if (state.stage === 3 && !state.reviewMode) {
          if (!validateStage3()) return;
          enterReviewMode();
        } else if (state.stage === 3 && state.reviewMode) {
          if (!canAddToBag()) return;
          onAddToBagClick();
        }
      });
    }

    // Back button — stage-aware dispatch
    if (els.backBtn) {
      els.backBtn.addEventListener('click', function () {
        if (state.stage === 2) {
          goToStage(1);
        } else if (state.stage === 3) {
          if (state.reviewMode) exitReviewMode();
          else goToStage(2);
        }
      });
    }

    // ---- Wire Stage 3 form controls ONCE at init ---------------
    // The Stage 3 shell is server-rendered; content updates via
    // syncStage3Views(). Event handlers attached here fire even
    // when Stage 3 is hidden (no-op if state.stage !== 3).

    if (els.blendNameInput) {
      els.blendNameInput.addEventListener('input', function () {
        state.blendName = String(els.blendNameInput.value || '');
        syncStage3Views();
      });
    }

    els.primaryFormatRadios.forEach(function (radio) {
      radio.addEventListener('change', function () {
        if (radio.checked) {
          // V.3C: three first-class formats — perfume / oil / set.
          // Matching-format toggle is retired; SET is now its own card.
          var v = radio.value;
          state.primaryFormat = (v === 'oil' || v === 'set') ? v : 'perfume';
          state.addMatchingFormat = false;
          syncStage3Views();
        }
      });
    });

    if (els.matchingToggle) {
      els.matchingToggle.addEventListener('change', function () {
        // Legacy path — matching-toggle no longer rendered in V.3C.
        // Kept defensive in case any downstream template still emits it.
        state.addMatchingFormat = !!els.matchingToggle.checked;
        syncStage3Views();
      });
    }

    applyView();

    // -----------------------------------------------------------
    // Stage 1 selection handling (unchanged from IV.2A-R)
    // -----------------------------------------------------------

    function onCardToggle(id) {
      var idx = state.selection.indexOf(id);
      if (idx >= 0) {
        state.selection.splice(idx, 1);
      } else {
        if (state.selection.length >= MAX_SELECTIONS) return;
        state.selection.push(id);
      }
      applyView();
    }

    // -----------------------------------------------------------
    // Stage transitions
    // -----------------------------------------------------------

    function goToStage(target) {
      if (target !== 1 && target !== 2 && target !== 3) return;

      // Ensure ratios exist for stages that need them, before rendering.
      if (target === 2 || target === 3) {
        ensureRatiosForCurrentSelection();
      }

      // Any stage transition returns Stage 3 to editable (not review).
      state.reviewMode = false;
      state.stage = target;

      // Rail state
      els.railItems.forEach(function (li) {
        var s = parseInt(li.getAttribute('data-ritual-rail-stage'), 10);
        li.classList.remove('is-active', 'is-locked', 'is-completed');
        if (s === state.stage) {
          li.classList.add('is-active');
          li.setAttribute('aria-current', 'step');
        } else {
          li.removeAttribute('aria-current');
          if (s < state.stage) {
            li.classList.add('is-completed');
            li.removeAttribute('aria-disabled');
          } else {
            li.classList.add('is-locked');
            li.setAttribute('aria-disabled', 'true');
          }
        }
      });

      // Panel visibility
      els.stagePanels.forEach(function (panel) {
        var s = parseInt(panel.getAttribute('data-ritual-stage-panel'), 10);
        panel.hidden = (s !== state.stage);
      });

      workspace.setAttribute('data-ritual-stage', String(state.stage));

      // Stage-specific renderers
      if (state.stage === 2) {
        renderBalanceStage();
      } else if (state.stage === 3) {
        // Ensure Stage 3 editable is visible, final hidden.
        if (els.stage3Editable) els.stage3Editable.hidden = false;
        if (els.stage3Final) els.stage3Final.hidden = true;
        syncStage3Views();
      }

      updateActionBarButtons();
      updateActionBarStatus();
    }

    function enterReviewMode() {
      state.reviewMode = true;
      state.cartPending = false; // fresh entry
      workspace.setAttribute('data-ritual-review-mode', 'true');
      if (els.stage3Editable) els.stage3Editable.hidden = true;
      if (els.stage3Final) els.stage3Final.hidden = false;
      clearCartError();
      renderStage3Final();
      updateActionBarButtons();
      updateActionBarStatus();
    }

    function exitReviewMode() {
      state.reviewMode = false;
      state.cartPending = false; // don't strand pending flag on Edit
      workspace.removeAttribute('data-ritual-review-mode');
      if (els.stage3Editable) els.stage3Editable.hidden = false;
      if (els.stage3Final) els.stage3Final.hidden = true;
      clearCartError();
      syncStage3Views();
      updateActionBarButtons();
      updateActionBarStatus();
    }

    /**
     * Continue + Back button visibility, label, and enabled state
     * per (stage, reviewMode). Called after every stage transition
     * and after Stage 3 form/state changes that affect validity.
     */
    function updateActionBarButtons() {
      // Continue
      if (els.continueBtn) {
        if (state.stage === 1) {
          els.continueBtn.hidden = false;
          if (els.continueLabel) els.continueLabel.textContent = 'Set the balance';
          var canContinue = state.selection.length >= MIN_TO_CONTINUE
                         && state.selection.length <= MAX_SELECTIONS;
          els.continueBtn.disabled = !canContinue;
          els.continueBtn.setAttribute('aria-disabled', canContinue ? 'false' : 'true');
        } else if (state.stage === 2) {
          els.continueBtn.hidden = false;
          if (els.continueLabel) els.continueLabel.textContent = 'Name & format';
          els.continueBtn.disabled = false;
          els.continueBtn.setAttribute('aria-disabled', 'false');
        } else if (state.stage === 3 && !state.reviewMode) {
          els.continueBtn.hidden = false;
          if (els.continueLabel) els.continueLabel.textContent = 'Review your blend';
          var valid = validateStage3();
          els.continueBtn.disabled = !valid;
          els.continueBtn.setAttribute('aria-disabled', valid ? 'false' : 'true');
        } else if (state.stage === 3 && state.reviewMode) {
          // Review mode: Continue = ADD TO BAG.
          els.continueBtn.hidden = false;
          if (els.continueLabel) {
            els.continueLabel.textContent = state.cartPending ? 'Adding…' : 'Add to bag';
          }
          var canAdd = canAddToBag();
          els.continueBtn.disabled = !canAdd;
          els.continueBtn.setAttribute('aria-disabled', canAdd ? 'false' : 'true');
        }
      }

      // Back
      if (els.backBtn) {
        if (state.stage === 1) {
          els.backBtn.hidden = true;
        } else if (state.stage === 2) {
          els.backBtn.hidden = false;
          if (els.backLabel) els.backLabel.textContent = 'Back to scents';
        } else if (state.stage === 3 && !state.reviewMode) {
          els.backBtn.hidden = false;
          if (els.backLabel) els.backLabel.textContent = 'Back to balance';
        } else if (state.stage === 3 && state.reviewMode) {
          els.backBtn.hidden = false;
          if (els.backLabel) {
            els.backLabel.textContent = state.cartPending ? 'Edit' : 'Edit';
          }
          // Disable Edit while cart request is in flight.
          els.backBtn.disabled = !!state.cartPending;
          els.backBtn.setAttribute('aria-disabled', state.cartPending ? 'true' : 'false');
        }
      }
    }

    // -----------------------------------------------------------
    // Cart integration (Phase IV.3A)
    // -----------------------------------------------------------

    function canAddToBag() {
      if (!state.reviewMode) return false;
      if (state.cartPending) return false;
      if (!commerceReady()) return false;
      return validateStage3();
    }

    function commerceReady() {
      var c = state.commerce;
      return !!(c && c.ok
        && Number.isInteger(c.perfumeVariantId) && c.perfumeVariantId > 0
        && Number.isInteger(c.oilVariantId)     && c.oilVariantId > 0
        && Number.isInteger(c.setVariantId)     && c.setVariantId > 0);
    }

    /**
     * Map (primaryFormat, addMatchingFormat) → one of the three
     * RITUAL — CUSTOM BLEND variants. Returns { id, formatLabel,
     * variantTitle } or null when the format state is incomplete.
     */
    function resolveSelectedVariant() {
      var c = state.commerce;
      if (!c || !c.ok) return null;
      var p = state.primaryFormat;
      // V.3C: three first-class formats. Legacy addMatchingFormat still
      // resolves to SET so any pre-V.3C state remains valid.
      if (p === 'set' || (state.addMatchingFormat && (p === 'perfume' || p === 'oil'))) {
        return { id: c.setVariantId,     formatLabel: 'PERFUME + OIL SET', variantTitle: 'Perfume + Oil Set' };
      }
      if (p === 'perfume') {
        return { id: c.perfumeVariantId, formatLabel: '100 ML PERFUME',    variantTitle: '100 ml Perfume' };
      }
      if (p === 'oil') {
        return { id: c.oilVariantId,     formatLabel: '15 ML PERFUME OIL', variantTitle: '15 ml Perfume Oil' };
      }
      return null;
    }

    /**
     * Build the single /cart/add.js line item for the current
     * recipe. Exactly ONE cart line is produced per submission —
     * fee is baked into the variant price ($85 / $55 / $130).
     * Source fragrance variants are never submitted as merchandise.
     */
    function buildRecipeItem(recipeId) {
      var resolved = resolveSelectedVariant();
      if (!resolved) throw new Error('variant resolution failed');
      var n = state.selection.length;

      var formulaParts = [];
      var vaultParts = [];
      for (var i = 0; i < n; i++) {
        var id = state.selection[i];
        var rec = data[id] || {};
        var pct = state.ratios[id] || 0;
        formulaParts.push(pct + '% ' + String(rec.name || '').toUpperCase());
        // "Vault-M-03" → "M-03"; leave already-short codes untouched.
        var shortVault = String(rec.vault || '').replace(/^vault[-\s]?/i, '').toUpperCase();
        vaultParts.push(pct + '% ' + shortVault);
      }

      return {
        id: resolved.id,
        quantity: 1,
        properties: {
          'Blend Name':    String(state.blendName || '').trim(),
          'Formula':       formulaParts.join(' · '),
          'Vault Formula': vaultParts.join(' · '),
          'Recipe ID':     recipeId
        }
      };
    }

    /**
     * Locate Dawn's cart-drawer (or cart-notification) so we can
     * reuse its section-render pipeline instead of reinventing one.
     * Returns { el, sectionIds } or nulls if not present.
     */
    function getDawnCart() {
      var el = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      if (!el) return { el: null, sectionIds: null };
      var sectionIds = null;
      if (typeof el.getSectionsToRender === 'function') {
        try {
          sectionIds = el.getSectionsToRender().map(function (s) { return s.id; });
        } catch (e) { /* leave null */ }
      }
      // Fallback set — safe defaults that render Dawn's cart UI.
      if (!sectionIds || sectionIds.length === 0) {
        sectionIds = ['cart-drawer', 'cart-icon-bubble'];
      }
      return { el: el, sectionIds: sectionIds };
    }

    function onAddToBagClick() {
      if (!canAddToBag()) return;
      if (state.cartPending) return;

      var recipeId = generateRecipeId();
      var item;
      try {
        item = buildRecipeItem(recipeId);
      } catch (buildErr) {
        console.error('[ritual] payload build failed', buildErr);
        showCartError('We couldn\'t add this blend. Try again.');
        return;
      }

      // Enter pending state
      state.cartPending = true;
      clearCartError();
      updateActionBarButtons();

      var cart = getDawnCart();
      var body = { items: [item] };
      if (cart.sectionIds) {
        body.sections = cart.sectionIds.join(',');
        body.sections_url = window.location.pathname;
      }

      var url = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
                ? (window.Shopify.routes.root + 'cart/add.js')
                : '/cart/add.js';

      fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(body)
      })
      .then(function (response) {
        return response.json().then(function (data) {
          return { ok: response.ok, status: response.status, data: data };
        });
      })
      .then(function (result) {
        if (!result.ok || (result.data && result.data.status)) {
          var msg = (result.data && (result.data.description || result.data.message))
                    || 'We couldn\'t add this blend. Try again.';
          console.error('[ritual] cart/add.js rejected', {
            status: result.status,
            body: result.data,
            item: item
          });
          throw new Error(msg);
        }
        // Clear any stale `is-empty` class on the outer Dawn cart
        // element BEFORE renderContents runs. Dawn's renderContents
        // replaces #CartDrawer's innerHTML but never touches classes
        // on the <cart-drawer> wrapper, and Dawn's own open() reads
        // .classList.contains('is-empty') to pick the focus-trap
        // target. Mirrors product-form.js's finally() cleanup.
        if (cart.el && cart.el.classList && cart.el.classList.contains('is-empty')) {
          cart.el.classList.remove('is-empty');
        }
        // Success — refresh Dawn cart via its own section pipeline.
        if (cart.el && typeof cart.el.renderContents === 'function' && result.data.sections) {
          try {
            cart.el.renderContents(result.data);
          } catch (rerr) {
            console.warn('[ritual] cart.renderContents failed', rerr);
          }
        }
        // Fire Dawn's pub-sub so the header cart bubble + any
        // other subscribers refresh naturally.
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'maddog-ritual-builder',
            cartData: result.data
          });
        }
        // If a drawer variant exists, open it so the customer sees
        // the new line immediately.
        if (cart.el && cart.el.tagName && cart.el.tagName.toLowerCase() === 'cart-drawer'
            && typeof cart.el.open === 'function') {
          try { cart.el.open(); } catch (oerr) { /* no-op */ }
        }
        state.cartPending = false;
        updateActionBarButtons();
      })
      .catch(function (err) {
        console.error('[ritual] add-to-bag failed', err);
        state.cartPending = false;
        showCartError((err && err.message)
                      ? err.message
                      : 'We couldn\'t add this blend. Try again.');
        updateActionBarButtons();
      });
    }

    function showCartError(msg) {
      if (!els.finalCartError) return;
      els.finalCartError.textContent = msg || 'We couldn\'t add this blend. Try again.';
      els.finalCartError.classList.remove('is-success');
      els.finalCartError.hidden = false;
    }

    function clearCartError() {
      if (!els.finalCartError) return;
      els.finalCartError.hidden = true;
      els.finalCartError.textContent = '';
      els.finalCartError.classList.remove('is-success');
    }

    // -----------------------------------------------------------
    // Ratio state management
    // -----------------------------------------------------------

    function ensureRatiosForCurrentSelection() {
      var key = state.selection.join(',');
      // Preserve ratios if selection composition/order is identical.
      if (state.ratiosSelectionKey === key
          && state.selection.length > 0
          && ratiosMatchSelection()) {
        return;
      }
      // Reset to defaults.
      state.ratios = {};
      var n = state.selection.length;
      if (n === 2) {
        state.ratios[state.selection[0]] = 50;
        state.ratios[state.selection[1]] = 50;
      } else if (n === 3) {
        // Earliest carries the +1 remainder → 34 / 33 / 33
        state.ratios[state.selection[0]] = 34;
        state.ratios[state.selection[1]] = 33;
        state.ratios[state.selection[2]] = 33;
      }
      state.ratiosSelectionKey = key;
    }

    function ratiosMatchSelection() {
      var keys = Object.keys(state.ratios);
      if (keys.length !== state.selection.length) return false;
      for (var i = 0; i < state.selection.length; i++) {
        if (!Object.prototype.hasOwnProperty.call(state.ratios, state.selection[i])) return false;
      }
      return true;
    }

    /**
     * Adjacent-pair setter. Every action affects at most TWO scents;
     * the untouched scent (r3 when editing r1/r2 via handle1/scent1,
     * or r1 when editing r2/r3 via handle2/scent3) stays fixed.
     *
     * Actions:
     *   2 scents:
     *     scent1 | handle1 : r1 = clamp(1..99, v);  r2 = 100 - r1
     *     scent2           : r2 = clamp(1..99, v);  r1 = 100 - r2
     *
     *   3 scents:
     *     handle1 : b1 clamp(1..99-r3, v);  r1 = b1;       r2 = 100-b1-r3
     *     handle2 : b2 clamp(r1+1..99, v);  r2 = b2-r1;    r3 = 100-b2
     *     scent1  : r1 clamp(1..99-r3, v);  r2 = 100-r1-r3
     *     scent3  : r3 clamp(1..99-r1, v);  r2 = 100-r1-r3
     */
    function setRatioPair(action, value) {
      var n = state.selection.length;
      if (n < 2) return;
      var v = (typeof value === 'number' && !isNaN(value))
              ? Math.round(value) : 0;
      var id1 = state.selection[0];
      var id2 = state.selection[1];
      var id3 = state.selection[2];

      if (n === 2) {
        if (action === 'scent1' || action === 'handle1') {
          var c = Math.max(1, Math.min(99, v));
          state.ratios[id1] = c;
          state.ratios[id2] = 100 - c;
        } else if (action === 'scent2') {
          var c2 = Math.max(1, Math.min(99, v));
          state.ratios[id2] = c2;
          state.ratios[id1] = 100 - c2;
        }
        return;
      }

      // n === 3
      var r1 = state.ratios[id1] || 0;
      var r2 = state.ratios[id2] || 0;
      var r3 = state.ratios[id3] || 0;

      if (action === 'handle1') {
        var maxB1 = 99 - r3; // ensures r2 >= 1
        var b1 = Math.max(1, Math.min(maxB1, v));
        state.ratios[id1] = b1;
        state.ratios[id2] = 100 - b1 - r3;
        // r3 unchanged
      } else if (action === 'handle2') {
        var b1cur = r1;
        var b2 = Math.max(b1cur + 1, Math.min(99, v));
        state.ratios[id2] = b2 - b1cur;
        state.ratios[id3] = 100 - b2;
        // r1 unchanged
      } else if (action === 'scent1') {
        var maxR1 = 99 - r3;
        var nR1 = Math.max(1, Math.min(maxR1, v));
        state.ratios[id1] = nR1;
        state.ratios[id2] = 100 - nR1 - r3;
        // r3 unchanged
      } else if (action === 'scent3') {
        var maxR3 = 99 - r1;
        var nR3 = Math.max(1, Math.min(maxR3, v));
        state.ratios[id3] = nR3;
        state.ratios[id2] = 100 - r1 - nR3;
        // r1 unchanged
      }
    }

    function boundaryValue(handleIdx) {
      var id1 = state.selection[0];
      var id2 = state.selection[1];
      if (handleIdx === 1) return state.ratios[id1] || 0;
      if (handleIdx === 2) return (state.ratios[id1] || 0) + (state.ratios[id2] || 0);
      return 0;
    }

    // -----------------------------------------------------------
    // Stage 2 rendering
    // -----------------------------------------------------------

    // Element reference maps used by renderBalanceStage + syncViews.
    // instrumentRefs.segments[i] = { el, valueEl }
    // instrumentRefs.handles = [handle1RangeEl] or [handle1, handle2]
    // balanceRowRefs[id] = { row, isAuto: bool, numInput?, readonlyEl?, action? }
    var instrumentRefs = {};

    function renderBalanceStage() {
      var instrumentEl = els.instrument;
      var rowsEl = els.balanceRows;
      if (!instrumentEl || !rowsEl) return;

      instrumentEl.innerHTML = '';
      rowsEl.innerHTML = '';
      instrumentRefs = { segments: [], handles: [] };
      balanceRowRefs = {};

      var n = state.selection.length;
      if (n < 2) return;

      buildInstrument(n);
      buildScentRows(n);

      // Initial sync
      syncViews();
    }

    function buildInstrument(n) {
      var container = els.instrument;
      container.setAttribute('data-scent-count', String(n));

      // "Drag to adjust" affordance eyebrow (Phase V.3B). Presentation
      // only — communicates that the composition bar below is an
      // interactive instrument, not a static readout. Sits above the
      // track so it doesn't fight the segment values for space.
      var hint = document.createElement('div');
      hint.className = 'maddog-ritual-builder__instrument-hint';
      var hintKicker = document.createElement('span');
      hintKicker.className = '__hint-kicker';
      hintKicker.textContent = 'Balance instrument';
      var hintCue = document.createElement('span');
      hintCue.className = '__hint-cue';
      hintCue.setAttribute('aria-hidden', 'true');
      hintCue.textContent = n === 2
        ? '‹ Drag divider to adjust ›'
        : '‹ Drag either divider to adjust ›';
      hint.appendChild(hintKicker);
      hint.appendChild(hintCue);
      container.appendChild(hint);

      // Segments track
      var track = document.createElement('div');
      track.className = 'maddog-ritual-builder__instrument-track';

      state.selection.forEach(function (id, i) {
        var rec = data[id] || {};
        var val = state.ratios[id] || 0;

        var seg = document.createElement('div');
        seg.className = 'maddog-ritual-builder__instrument-segment';
        seg.setAttribute('data-index', String(i));
        seg.style.width = val + '%';

        var content = document.createElement('div');
        content.className = '__seg-content';

        var order = document.createElement('span');
        order.className = '__seg-order';
        order.textContent = pad2(i + 1);
        content.appendChild(order);

        var name = document.createElement('span');
        name.className = '__seg-name';
        name.textContent = String(rec.name || '');
        content.appendChild(name);

        var value = document.createElement('span');
        value.className = '__seg-value';
        value.textContent = val + '%';
        content.appendChild(value);

        seg.appendChild(content);
        track.appendChild(seg);

        instrumentRefs.segments.push({ el: seg, valueEl: value });
      });

      // Handles overlay (1 for 2-scent, 2 for 3-scent)
      var handlesLayer = document.createElement('div');
      handlesLayer.className = 'maddog-ritual-builder__instrument-handles';

      var handleCount = (n === 2) ? 1 : 2;
      for (var h = 1; h <= handleCount; h++) {
        var range = document.createElement('input');
        range.type = 'range';
        range.className = 'maddog-ritual-builder__instrument-handle';
        range.setAttribute('data-ritual-handle', String(h));
        range.min = '0';
        range.max = '100';
        range.step = '1';
        range.value = String(boundaryValue(h));

        // aria-label
        var recA = data[state.selection[h - 1]] || {};
        var recB = data[state.selection[h]] || {};
        var labelWord = (n === 2) ? 'Balance between ' : 'Boundary between ';
        range.setAttribute(
          'aria-label',
          labelWord + String(recA.name || '') + ' and ' + String(recB.name || '')
        );

        // Wire input
        (function (handleIdx, rangeEl) {
          rangeEl.addEventListener('input', function () {
            var v = parseInt(rangeEl.value, 10);
            if (isNaN(v)) return;
            var action = (n === 2)
                       ? 'handle1'
                       : (handleIdx === 1 ? 'handle1' : 'handle2');
            setRatioPair(action, v);
            syncViews();
          });
        })(h, range);

        handlesLayer.appendChild(range);
        instrumentRefs.handles.push(range);
      }

      container.appendChild(track);
      container.appendChild(handlesLayer);
    }

    function buildScentRows(n) {
      var container = els.balanceRows;

      state.selection.forEach(function (id, i) {
        var rec = data[id] || {};
        var val = state.ratios[id] || 0;
        var isMiddleOfThree = (n === 3 && i === 1);

        var li = document.createElement('li');
        li.className = 'maddog-ritual-builder__balance-row';
        if (isMiddleOfThree) li.classList.add('is-auto');
        li.setAttribute('data-ritual-ratio-id', id);

        var header = document.createElement('div');
        header.className = '__row-header';

        var order = document.createElement('span');
        order.className = '__row-order';
        order.textContent = pad2(i + 1);
        header.appendChild(order);

        if (rec.image) {
          var thumb = document.createElement('span');
          thumb.className = '__row-thumb';
          var img = document.createElement('img');
          img.src = String(rec.image);
          img.alt = '';
          img.loading = 'lazy';
          thumb.appendChild(img);
          header.appendChild(thumb);
        }

        var info = document.createElement('div');
        info.className = '__row-info';
        var nameEl = document.createElement('span');
        nameEl.className = '__row-name';
        nameEl.textContent = String(rec.name || '');
        info.appendChild(nameEl);
        if (rec.vault) {
          var vaultEl = document.createElement('span');
          vaultEl.className = '__row-vault';
          vaultEl.textContent = String(rec.vault);
          info.appendChild(vaultEl);
        }
        header.appendChild(info);

        var valueWrap = document.createElement('div');
        valueWrap.className = '__row-value';

        if (isMiddleOfThree) {
          valueWrap.classList.add('is-auto');

          var readonly = document.createElement('span');
          readonly.className = '__row-value-readonly';
          readonly.setAttribute('aria-live', 'polite');
          readonly.textContent = String(val);
          valueWrap.appendChild(readonly);

          var unit = document.createElement('span');
          unit.className = '__row-value-unit';
          unit.textContent = '%';
          valueWrap.appendChild(unit);

          var autoLabel = document.createElement('span');
          autoLabel.className = '__row-auto-label';
          autoLabel.textContent = 'Auto balance';
          valueWrap.appendChild(autoLabel);

          balanceRowRefs[id] = {
            row: li,
            isAuto: true,
            readonlyEl: readonly
          };
        } else {
          var numInput = document.createElement('input');
          numInput.type = 'number';
          numInput.className = '__ratio-number';
          numInput.min = '1';
          numInput.max = '99'; // dynamic max applied by syncViews
          numInput.step = '1';
          numInput.value = String(val);
          numInput.setAttribute('inputmode', 'numeric');
          numInput.setAttribute('aria-label', String(rec.name || 'scent') + ' percentage');
          numInput.setAttribute('data-ritual-ratio-num', id);
          valueWrap.appendChild(numInput);

          var unit2 = document.createElement('span');
          unit2.className = '__row-value-unit';
          unit2.textContent = '%';
          valueWrap.appendChild(unit2);

          // Determine the setRatioPair action this input triggers.
          var action;
          if (n === 2) {
            action = (i === 0) ? 'scent1' : 'scent2';
          } else { // n === 3
            action = (i === 0) ? 'scent1' : 'scent3';
          }

          (function (act, inp) {
            inp.addEventListener('change', function () {
              var raw = inp.value;
              var vv = (raw === '') ? state.ratios[id] : parseInt(raw, 10);
              if (isNaN(vv)) vv = state.ratios[id];
              setRatioPair(act, vv);
              syncViews();
            });
            inp.addEventListener('keydown', function (e) {
              if (e.key === 'Enter') {
                e.preventDefault();
                inp.blur();
              }
            });
          })(action, numInput);

          balanceRowRefs[id] = {
            row: li,
            isAuto: false,
            numInput: numInput,
            action: action
          };
        }
        header.appendChild(valueWrap);
        li.appendChild(header);
        container.appendChild(li);
      });
    }

    /**
     * Push current state.ratios out to all live view surfaces
     * (segments, handle inputs, scent-row inputs / readonlys,
     * preview column, totals). Also refreshes dynamic min/max
     * bounds so clamping stays visually consistent.
     */
    function syncViews() {
      var n = state.selection.length;
      if (n < 2) return;
      var id1 = state.selection[0];
      var id2 = state.selection[1];
      var id3 = state.selection[2];
      var r1 = state.ratios[id1] || 0;
      var r2 = state.ratios[id2] || 0;
      var r3 = state.ratios[id3] || 0;

      // Segments
      state.selection.forEach(function (id, i) {
        var val = state.ratios[id] || 0;
        var segRef = instrumentRefs.segments[i];
        if (segRef && segRef.el) {
          segRef.el.style.width = val + '%';
          if (segRef.valueEl) segRef.valueEl.textContent = val + '%';
        }
      });

      // Handle range inputs — sync value + refresh implicit
      // bounds via dispatched no-op set (native uses min/max but
      // clamping happens in the input handler already).
      if (instrumentRefs.handles[0]) {
        var b1 = boundaryValue(1);
        if (parseInt(instrumentRefs.handles[0].value, 10) !== b1) {
          instrumentRefs.handles[0].value = String(b1);
        }
      }
      if (instrumentRefs.handles[1]) {
        var b2 = boundaryValue(2);
        if (parseInt(instrumentRefs.handles[1].value, 10) !== b2) {
          instrumentRefs.handles[1].value = String(b2);
        }
      }

      // Balance rows
      state.selection.forEach(function (id) {
        var refs = balanceRowRefs[id];
        if (!refs) return;
        var val = state.ratios[id] || 0;

        if (refs.isAuto && refs.readonlyEl) {
          refs.readonlyEl.textContent = String(val);
        } else if (refs.numInput) {
          if (parseInt(refs.numInput.value, 10) !== val) {
            refs.numInput.value = String(val);
          }
          // Refresh dynamic max
          if (n === 2) {
            refs.numInput.max = '99';
          } else if (n === 3) {
            if (refs.action === 'scent1') refs.numInput.max = String(99 - r3);
            else if (refs.action === 'scent3') refs.numInput.max = String(99 - r1);
          }
        }
      });

      // Preview + totals
      updatePreviewRows();
      updateTotals();
    }

    function updatePreviewRows() {
      var container = els.previewRows;
      if (!container) return;
      container.innerHTML = '';

      state.selection.forEach(function (id, i) {
        var rec = data[id] || {};
        var val = state.ratios[id] || 0;
        var li = document.createElement('li');
        li.className = 'maddog-ritual-builder__preview-row';

        var order = document.createElement('span');
        order.className = '__preview-order';
        order.textContent = pad2(i + 1);
        li.appendChild(order);

        if (rec.image) {
          var thumb = document.createElement('span');
          thumb.className = '__preview-thumb';
          var img = document.createElement('img');
          img.src = String(rec.image);
          img.alt = '';
          img.loading = 'lazy';
          thumb.appendChild(img);
          li.appendChild(thumb);
        }

        var info = document.createElement('span');
        info.className = '__preview-info';
        var name = document.createElement('span');
        name.className = '__preview-name';
        name.textContent = String(rec.name || '');
        info.appendChild(name);
        if (rec.vault) {
          var vault = document.createElement('span');
          vault.className = '__preview-vault';
          vault.textContent = String(rec.vault);
          info.appendChild(vault);
        }
        li.appendChild(info);

        var pct = document.createElement('span');
        pct.className = '__preview-pct';
        pct.textContent = val + '%';
        li.appendChild(pct);

        container.appendChild(li);
      });
    }

    function updateTotals() {
      var total = 0;
      state.selection.forEach(function (id) {
        total += (state.ratios[id] || 0);
      });
      if (els.totalDisplay) els.totalDisplay.textContent = total + '%';
      if (els.previewTotal) els.previewTotal.textContent = total + '%';
    }

    // -----------------------------------------------------------
    // Stage 3: NAME & FORMAT (editable) + final review
    // -----------------------------------------------------------

    function validateStage3() {
      var name = String(state.blendName || '').trim();
      if (name.length < 1 || name.length > NAME_MAX_LEN) return false;
      if (!NAME_ALLOWED_PATTERN.test(name)) return false;
      var p = state.primaryFormat;
      if (p !== 'perfume' && p !== 'oil' && p !== 'set') return false;
      if (state.selection.length < MIN_TO_CONTINUE
          || state.selection.length > MAX_SELECTIONS) return false;
      return true;
    }

    function isNameCharValid() {
      var raw = String(state.blendName || '');
      var trimmed = raw.trim();
      if (trimmed === '') return true; // empty is not an error until submit
      return NAME_ALLOWED_PATTERN.test(trimmed);
    }

    function computePrice() {
      // V.3C: authoritative variant prices from the commerce payload
      // (fee is already baked into the RITUAL variant price — no
      // separate fee line). One selected format → one price line.
      var c = state.commerce || {};
      var p = state.primaryFormat;
      var lines = [];
      var total = 0;
      var effectiveSet = (p === 'set') || (state.addMatchingFormat && (p === 'perfume' || p === 'oil'));
      if (effectiveSet) {
        lines.push({ label: 'Perfume + Oil Set', price: Number(c.setVariantPrice) || 0 });
        total = lines[0].price;
      } else if (p === 'perfume') {
        lines.push({ label: '100 ml Perfume', price: Number(c.perfumeVariantPrice) || 0 });
        total = lines[0].price;
      } else if (p === 'oil') {
        lines.push({ label: '15 ml Perfume Oil', price: Number(c.oilVariantPrice) || 0 });
        total = lines[0].price;
      }
      return { lines: lines, total: total };
    }

    /**
     * Push Stage 3 state out to every editable-state surface:
     * name input value + count + preview + error, format radios +
     * card classes, matching section visibility + toggle + label,
     * review sidebar (formula, name, format list, price lines,
     * total), and the Continue button enabled state.
     */
    function syncStage3Views() {
      // Name input value + count
      if (els.blendNameInput && els.blendNameInput.value !== state.blendName) {
        els.blendNameInput.value = state.blendName;
      }
      var trimmed = String(state.blendName || '').trim();
      if (els.nameCount) {
        els.nameCount.textContent = trimmed.length + ' / ' + NAME_MAX_LEN;
      }

      // Name preview — deliberate uppercase placeholder before entry.
      if (els.namePreviewValue) {
        if (trimmed === '') {
          els.namePreviewValue.textContent = 'YOUR NAME';
          els.namePreviewValue.setAttribute('data-ritual-empty', 'true');
        } else {
          els.namePreviewValue.textContent = trimmed.toUpperCase();
          els.namePreviewValue.removeAttribute('data-ritual-empty');
        }
      }

      // Name error hint
      if (els.nameError) {
        els.nameError.hidden = isNameCharValid();
      }

      // Format radios + card classes
      els.primaryFormatRadios.forEach(function (radio) {
        var isSelected = state.primaryFormat && radio.value === state.primaryFormat;
        if (radio.checked !== !!isSelected) radio.checked = !!isSelected;
        var card = radio.closest('.maddog-ritual-builder__format-section .__format-card');
        if (card) card.classList.toggle('is-selected', !!isSelected);
      });

      // V.3C: matching-format section is retired — SET is a first-class
      // radio card. These branches remain defensively for older DOM.
      if (els.matchingSection) els.matchingSection.hidden = true;
      if (els.matchingToggle && els.matchingToggle.checked) {
        els.matchingToggle.checked = false;
      }

      // Review sidebar — formula list
      renderStage3ReviewFormula();

      // Review sidebar — name
      if (els.stage3ReviewName) {
        if (trimmed === '') {
          els.stage3ReviewName.textContent = 'Not set';
          els.stage3ReviewName.setAttribute('data-ritual-empty', 'true');
        } else {
          els.stage3ReviewName.textContent = trimmed;
          els.stage3ReviewName.removeAttribute('data-ritual-empty');
        }
      }

      // Review sidebar — format list
      renderStage3ReviewFormat();

      // Review sidebar — price breakdown + total
      renderStage3PriceBreakdown();

      // Continue button state (validation-dependent)
      updateActionBarButtons();
    }

    function renderStage3ReviewFormula() {
      var container = els.stage3ReviewFormula;
      if (!container) return;
      container.innerHTML = '';
      state.selection.forEach(function (id, i) {
        var rec = data[id] || {};
        var val = state.ratios[id] || 0;
        var li = document.createElement('li');
        li.className = '__review-formula-row';

        var order = document.createElement('span');
        order.className = '__review-formula-order';
        order.textContent = pad2(i + 1);
        li.appendChild(order);

        var info = document.createElement('span');
        info.className = '__review-formula-info';
        var name = document.createElement('span');
        name.className = '__review-formula-name';
        name.textContent = String(rec.name || '');
        info.appendChild(name);
        if (rec.vault) {
          var vault = document.createElement('span');
          vault.className = '__review-formula-vault';
          vault.textContent = String(rec.vault);
          info.appendChild(vault);
        }
        li.appendChild(info);

        var pct = document.createElement('span');
        pct.className = '__review-formula-pct';
        pct.textContent = val + '%';
        li.appendChild(pct);

        container.appendChild(li);
      });
    }

    function renderStage3ReviewFormat() {
      var container = els.stage3ReviewFormat;
      if (!container) return;
      container.innerHTML = '';
      var p = state.primaryFormat;
      if (!p) {
        var empty = document.createElement('li');
        empty.className = '__review-format-empty';
        empty.textContent = 'Not selected';
        container.appendChild(empty);
        return;
      }
      // Phase V.3C-2: concise two-line format label — variant name
      // above, size below. Cart property mapping unchanged.
      var effectiveSet = (p === 'set') || (state.addMatchingFormat && (p === 'perfume' || p === 'oil'));
      var nameLine;
      var sizeLine;
      if (effectiveSet) {
        nameLine = 'Perfume + Oil Set';
        sizeLine = '100 ml + 15 ml';
      } else if (p === 'perfume') {
        nameLine = 'Perfume';
        sizeLine = '100 ml';
      } else {
        nameLine = 'Perfume Oil';
        sizeLine = '15 ml';
      }
      var l1 = document.createElement('li');
      l1.className = '__review-format-line __review-format-line--name';
      l1.textContent = nameLine;
      container.appendChild(l1);
      var l2 = document.createElement('li');
      l2.className = '__review-format-line __review-format-line--size';
      l2.textContent = sizeLine;
      container.appendChild(l2);
    }

    function renderStage3PriceBreakdown() {
      var container = els.stage3PriceLines;
      var totalEl = els.stage3PriceTotal;
      if (!container || !totalEl) return;
      container.innerHTML = '';

      var price = computePrice();
      if (price.lines.length === 0) {
        totalEl.textContent = '—';
        return;
      }
      price.lines.forEach(function (line) {
        var li = document.createElement('li');
        li.className = '__review-price-line';
        var label = document.createElement('span');
        label.className = '__review-price-label';
        label.textContent = line.label;
        li.appendChild(label);
        var value = document.createElement('span');
        value.className = '__review-price-value';
        value.textContent = formatMoneyAUD(line.price);
        li.appendChild(value);
        container.appendChild(li);
      });
      totalEl.textContent = formatMoneyAUD(price.total);
    }

    function renderStage3Final() {
      // Name
      if (els.finalName) {
        els.finalName.textContent = String(state.blendName || '').trim().toUpperCase();
      }

      // Formula
      if (els.finalFormula) {
        els.finalFormula.innerHTML = '';
        state.selection.forEach(function (id, i) {
          var rec = data[id] || {};
          var val = state.ratios[id] || 0;
          var li = document.createElement('li');
          li.className = '__final-formula-row';

          var pct = document.createElement('span');
          pct.className = '__final-formula-pct';
          pct.textContent = val + '%';
          li.appendChild(pct);

          var name = document.createElement('span');
          name.className = '__final-formula-name';
          name.textContent = String(rec.name || '');
          li.appendChild(name);

          if (rec.vault) {
            var vault = document.createElement('span');
            vault.className = '__final-formula-vault';
            vault.textContent = String(rec.vault);
            li.appendChild(vault);
          }

          els.finalFormula.appendChild(li);
        });
      }

      // Formats
      if (els.finalFormats) {
        els.finalFormats.innerHTML = '';
        var price = computePrice();
        price.lines.forEach(function (line) {
          if (line.label === 'Custom blend fee') return; // shown separately
          var li = document.createElement('li');
          li.className = '__final-format-line';
          li.textContent = line.label;
          els.finalFormats.appendChild(li);
        });
      }

      // Total
      if (els.finalTotal) {
        var p = computePrice();
        els.finalTotal.textContent = formatMoneyAUD(p.total);
      }
    }

    // -----------------------------------------------------------
    // Stage 1 view (unchanged from IV.2A-R aside from status text)
    // -----------------------------------------------------------

    function applyView() {
      var selectedSet = new Set(state.selection);
      var atMax = state.selection.length >= MAX_SELECTIONS;
      var visibleCount = 0;

      for (var i = 0; i < els.cardItems.length; i++) {
        var item = els.cardItems[i];
        var id = item.getAttribute('data-ritual-card-id');
        var letter = item.getAttribute('data-ritual-letter') || '';
        var haystack = item.getAttribute('data-ritual-search') || '';

        var letterOk = !state.letter || letter === state.letter;
        var queryOk = !state.query || haystack.indexOf(state.query) !== -1;
        var visible = letterOk && queryOk;
        item.hidden = !visible;
        if (visible) visibleCount++;

        var isSelected = selectedSet.has(id);
        var btn = item.querySelector('[data-ritual-select]');
        if (isSelected) {
          item.classList.add('is-selected');
          var order = state.selection.indexOf(id) + 1;
          var numEl = item.querySelector('[data-ritual-select-num]');
          if (numEl) numEl.textContent = pad2(order);
          if (btn) btn.setAttribute('aria-pressed', 'true');
        } else {
          item.classList.remove('is-selected');
          var numEl2 = item.querySelector('[data-ritual-select-num]');
          if (numEl2) numEl2.textContent = '';
          if (btn) btn.setAttribute('aria-pressed', 'false');
        }

        if (atMax && !isSelected) {
          item.classList.add('is-unavailable');
          if (btn) btn.setAttribute('aria-disabled', 'true');
        } else {
          item.classList.remove('is-unavailable');
          if (btn) btn.removeAttribute('aria-disabled');
        }
      }

      if (els.visibleCount) {
        var hasFilter = !!state.query || !!state.letter;
        var word = visibleCount === 1 ? ' scent' : ' scents';
        els.visibleCount.textContent = visibleCount + word + (hasFilter ? ' found' : '');
      }
      if (els.emptyEl) els.emptyEl.hidden = visibleCount > 0;

      if (els.selectedCount) {
        els.selectedCount.textContent =
          state.selection.length + ' / ' + MAX_SELECTIONS + ' selected';
      }

      renderFormulaSlots();
      renderStatusLabels();
      updateActionBarStatus();
      updateActionBarButtons();

      // If we're currently on a later stage and selection dropped
      // below 2 (e.g. via keyboard delete from Stage 1 which is
      // unreachable while Stage 2/3 shown — kept as a safety net)
      if (state.stage !== 1 && state.selection.length < MIN_TO_CONTINUE) {
        goToStage(1);
      }
    }

    function renderFormulaSlots() {
      for (var slotIdx = 0; slotIdx < 3; slotIdx++) {
        var slotNum = slotIdx + 1;
        var slotEl = document.querySelector('[data-ritual-slot="' + slotNum + '"]');
        var bodyEl = document.querySelector('[data-ritual-slot-body="' + slotNum + '"]');
        if (!slotEl || !bodyEl) continue;

        var id = state.selection[slotIdx];
        if (!id) {
          slotEl.classList.remove('is-filled');
          bodyEl.innerHTML = '';
          var emptyText = document.createElement('span');
          emptyText.className = '__slot-empty';
          emptyText.textContent = (slotNum === 3) ? 'Optional' : 'Select a scent';
          bodyEl.appendChild(emptyText);
          continue;
        }

        var rec = data[id] || {};
        slotEl.classList.add('is-filled');
        bodyEl.innerHTML = '';
        var wrap = document.createElement('div');
        wrap.className = '__slot-populated';

        if (rec.image) {
          var thumb = document.createElement('span');
          thumb.className = '__slot-thumb';
          var img = document.createElement('img');
          img.src = String(rec.image);
          img.alt = '';
          img.loading = 'lazy';
          thumb.appendChild(img);
          wrap.appendChild(thumb);
        }

        var info = document.createElement('span');
        info.className = '__slot-info';
        var nameEl = document.createElement('span');
        nameEl.className = '__slot-name';
        nameEl.textContent = String(rec.name || '');
        info.appendChild(nameEl);
        if (rec.vault) {
          var vaultEl = document.createElement('span');
          vaultEl.className = '__slot-vault';
          vaultEl.textContent = String(rec.vault);
          info.appendChild(vaultEl);
        }
        wrap.appendChild(info);

        var remove = document.createElement('button');
        remove.type = 'button';
        remove.className = '__slot-remove';
        remove.setAttribute('data-ritual-blend-remove', id);
        remove.setAttribute(
          'aria-label',
          'Remove ' + String(rec.name || 'scent') + ' from your blend'
        );
        remove.textContent = '×';
        wrap.appendChild(remove);

        bodyEl.appendChild(wrap);
      }
    }

    function renderStatusLabels() {
      var n = state.selection.length;
      if (els.formulaStatus) {
        var msg;
        if (n === 0) msg = 'Need 2 scents';
        else if (n === 1) msg = 'Need 1 more scent';
        else if (n === 2) msg = 'Ready to balance';
        else msg = '3 / 3 selected';
        els.formulaStatus.textContent = msg;
        els.formulaStatus.classList.toggle('is-ready', n >= MIN_TO_CONTINUE);
      }
    }

    function updateActionBarStatus() {
      if (!els.actionbarStatus) return;
      if (state.stage === 1) {
        els.actionbarStatus.textContent = state.selection.length + ' of 3 selected';
      } else if (state.stage === 2) {
        els.actionbarStatus.textContent = 'Total 100%';
      } else if (state.stage === 3) {
        if (state.reviewMode) {
          els.actionbarStatus.textContent = 'Ready to add';
        } else {
          els.actionbarStatus.textContent = 'Blend name + format';
        }
      } else {
        els.actionbarStatus.textContent = '';
      }
    }

    function updateMfuCounts() {
      var counts = { ALL: 0, M: 0, F: 0, U: 0 };
      var ids = Object.keys(data);
      for (var i = 0; i < ids.length; i++) {
        counts.ALL++;
        var L = String((data[ids[i]] && data[ids[i]].letter) || '').toUpperCase();
        if (Object.prototype.hasOwnProperty.call(counts, L)) counts[L]++;
      }
      Object.keys(els.mfuCounts).forEach(function (k) {
        var el = els.mfuCounts[k];
        if (el) el.textContent = String(counts[k]);
      });
    }
  }

  function pad2(n) {
    n = String(n);
    return n.length < 2 ? '0' + n : n;
  }

  /**
   * Parse the Liquid-emitted #maddog-ritual-commerce-data JSON
   * for the single-product architecture (ritual-custom-blend
   * with three FORMAT variants: perfume / oil / set). Fee is
   * baked into variant prices; there is no separate fee variant.
   * When any variant ID is missing or any variant price fails
   * the bundled-total invariant, `ok === false` and the
   * controller keeps ADD TO BAG disabled.
   */
  function parseCommerceData() {
    var out = {
      ok: false,
      perfumeVariantId: null,
      oilVariantId: null,
      setVariantId: null,
      perfumeVariantPrice: null,
      oilVariantPrice: null,
      setVariantPrice: null,
      currency: null,
      reason: null
    };
    var el = document.getElementById('maddog-ritual-commerce-data');
    if (!el) { out.reason = 'commerce-data element missing'; return out; }
    var raw;
    try { raw = JSON.parse(el.textContent || el.innerText || '{}'); }
    catch (e) { out.reason = 'commerce-data JSON parse failed'; return out; }

    var pId = Number(raw.perfumeVariantId);
    var oId = Number(raw.oilVariantId);
    var sId = Number(raw.setVariantId);
    var pPr = Number(raw.perfumeVariantPrice);
    var oPr = Number(raw.oilVariantPrice);
    var sPr = Number(raw.setVariantPrice);

    out.perfumeVariantId    = Number.isInteger(pId) && pId > 0 ? pId : null;
    out.oilVariantId        = Number.isInteger(oId) && oId > 0 ? oId : null;
    out.setVariantId        = Number.isInteger(sId) && sId > 0 ? sId : null;
    out.perfumeVariantPrice = isFinite(pPr) ? pPr : null;
    out.oilVariantPrice     = isFinite(oPr) ? oPr : null;
    out.setVariantPrice     = isFinite(sPr) ? sPr : null;
    out.currency = String(raw.currency || '');

    if (!out.perfumeVariantId || !out.oilVariantId || !out.setVariantId) {
      out.reason = 'one or more variant IDs missing (ritual-custom-blend variants not found?)';
      return out;
    }
    // Variant prices must equal the bundled totals derived from
    // display constants + baked-in fee.
    var expectedPerfume = PRICE_PERFUME + PRICE_FEE;             // $85
    var expectedOil     = PRICE_OIL     + PRICE_FEE;             // $55
    var expectedSet     = PRICE_PERFUME + PRICE_OIL + PRICE_FEE; // $130
    if (Math.abs(out.perfumeVariantPrice - expectedPerfume) > 0.01
        || Math.abs(out.oilVariantPrice - expectedOil)     > 0.01
        || Math.abs(out.setVariantPrice - expectedSet)     > 0.01) {
      out.reason = 'variant price mismatch vs bundled expectation ('
                 + 'perfume=' + out.perfumeVariantPrice + ' expected ' + expectedPerfume
                 + ', oil=' + out.oilVariantPrice + ' expected ' + expectedOil
                 + ', set=' + out.setVariantPrice + ' expected ' + expectedSet + ')';
      return out;
    }
    out.ok = true;
    return out;
  }

  function collectElements() {
    return {
      cardItems: Array.prototype.slice.call(
        document.querySelectorAll('[data-ritual-card-id]')
      ),
      finder: document.querySelector('[data-ritual-finder]'),
      mfuButtons: Array.prototype.slice.call(
        document.querySelectorAll('[data-ritual-mfu-value]')
      ),
      selectedCount: document.querySelector('[data-ritual-selected-count]'),
      visibleCount: document.querySelector('[data-ritual-visible-count]'),
      emptyEl: document.querySelector('[data-ritual-empty]'),
      continueBtn: document.querySelector('[data-ritual-continue]'),
      continueLabel: document.querySelector('[data-ritual-continue-label]'),
      backBtn: document.querySelector('[data-ritual-back]'),
      backLabel: document.querySelector('[data-ritual-back-label]'),
      railItems: Array.prototype.slice.call(
        document.querySelectorAll('[data-ritual-rail-stage]')
      ),
      stagePanels: Array.prototype.slice.call(
        document.querySelectorAll('[data-ritual-stage-panel]')
      ),
      // Stage 2 controls
      balanceRows: document.querySelector('[data-ritual-balance-rows]'),
      instrument: document.querySelector('[data-ritual-instrument]'),
      previewRows: document.querySelector('[data-ritual-preview-rows]'),
      previewTotal: document.querySelector('[data-ritual-preview-total]'),
      totalDisplay: document.querySelector('[data-ritual-total]'),
      // Stage 3 — editable state
      stage3Editable: document.querySelector('[data-ritual-stage3-editable]'),
      stage3Final: document.querySelector('[data-ritual-stage3-final]'),
      blendNameInput: document.querySelector('[data-ritual-blend-name]'),
      nameCount: document.querySelector('[data-ritual-name-count]'),
      nameError: document.querySelector('[data-ritual-name-error]'),
      namePreviewValue: document.querySelector('[data-ritual-name-preview-value]'),
      primaryFormatRadios: Array.prototype.slice.call(
        document.querySelectorAll('[data-ritual-primary-format]')
      ),
      matchingSection: document.querySelector('[data-ritual-matching-section]'),
      matchingToggle: document.querySelector('[data-ritual-matching-toggle]'),
      matchingLabel: document.querySelector('[data-ritual-matching-label]'),
      // Stage 3 — review sidebar (inside editable)
      stage3ReviewFormula: document.querySelector('[data-ritual-stage3-review-formula]'),
      stage3ReviewName: document.querySelector('[data-ritual-stage3-review-name]'),
      stage3ReviewFormat: document.querySelector('[data-ritual-stage3-review-format]'),
      stage3PriceLines: document.querySelector('[data-ritual-stage3-price-lines]'),
      stage3PriceTotal: document.querySelector('[data-ritual-stage3-price-total]'),
      // Stage 3 — final review
      finalName: document.querySelector('[data-ritual-final-name]'),
      finalFormula: document.querySelector('[data-ritual-final-formula]'),
      finalFormats: document.querySelector('[data-ritual-final-formats]'),
      finalTotal: document.querySelector('[data-ritual-final-total]'),
      finalCartError: document.querySelector('[data-ritual-final-cart-error]'),
      // Formula sidebar (stage 1)
      formulaStatus: document.querySelector('[data-ritual-formula-status]'),
      actionbarStatus: document.querySelector('[data-ritual-actionbar-status]'),
      mfuCounts: {
        ALL: document.querySelector('[data-ritual-mfu-count="ALL"]'),
        M:   document.querySelector('[data-ritual-mfu-count="M"]'),
        F:   document.querySelector('[data-ritual-mfu-count="F"]'),
        U:   document.querySelector('[data-ritual-mfu-count="U"]')
      }
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
